import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart2,
  Users,
  Package,
  ShoppingCart,
  FileText,
  DollarSign,
  Settings,
  Plug,
  Key,
  PieChart,
  LogOut,
  Boxes
} from 'lucide-react';

interface SidebarProps {
  onSignOut: () => Promise<void>;
}

const Sidebar: React.FC<SidebarProps> = ({ onSignOut }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const currentView = location.pathname.split('/')[2] || 'dashboard';

  const menuItems = [
    { id: 'dashboard', icon: BarChart2, label: 'Dashboard' },
    { id: 'customers', icon: Users, label: 'Clientes' },
    { id: 'products', icon: Package, label: 'Produtos' },
    { id: 'sales', icon: ShoppingCart, label: 'Vendas' },
    { id: 'fiscal', icon: FileText, label: 'Fiscal' },
    { id: 'financial', icon: DollarSign, label: 'Financeiro' },
    { id: 'reports', icon: PieChart, label: 'Relatórios' },
    { id: 'users', icon: Key, label: 'Usuários' },
    { id: 'settings', icon: Settings, label: 'Configurações' },
    { id: 'integrations', icon: Plug, label: 'Integrações' }
  ];

  const handleViewChange = (view: string) => {
    navigate(`/app/${view}`);
  };

  return (
    <aside className="fixed left-0 top-0 h-screen w-20 bg-white border-r border-gray-200 flex flex-col items-center py-6 shadow-sm">
      {/* Logo */}
      <div className="mb-8">
        <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center shadow-soft">
          <Boxes className="h-6 w-6 text-white" />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 w-full px-2">
        <div className="space-y-3">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = currentView === item.id;
            
            return (
              <button
                key={item.id}
                onClick={() => handleViewChange(item.id)}
                className={`w-full h-12 flex items-center justify-center rounded-xl transition-all duration-200 relative group
                  ${isActive 
                    ? 'bg-blue-50 text-blue-600' 
                    : 'text-gray-500 hover:bg-gray-50 hover:text-blue-600'
                  }`}
              >
                <Icon className={`h-5 w-5 transition-transform duration-200 ${
                  isActive ? 'scale-110' : 'group-hover:scale-110'
                }`} />
                
                {/* Tooltip */}
                <span className="absolute left-full ml-4 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Logout Button */}
      <div className="mt-8 w-full px-2">
        <button
          onClick={onSignOut}
          className="w-full h-12 flex items-center justify-center text-gray-500 hover:text-red-600 transition-colors rounded-xl hover:bg-red-50 group relative"
        >
          <LogOut className="h-5 w-5" />
          
          {/* Tooltip */}
          <span className="absolute left-full ml-4 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
            Sair
          </span>
        </button>
      </div>

      {/* Version */}
      <div className="mt-4 text-xs text-gray-400">
        v1.0.0
      </div>
    </aside>
  );
};

export default Sidebar;